import {TileData} from "./remoteConstants.model";

// Authored map document — the TS mirror of MapDocument.cs (Rift.Core, shared
// by the Unity client and the Remote backend). One JSON blob per published
// version in the 'maps' blob container, enumerated via the maps.json manifest.
//
// Maps are branch-agnostic: tilePalette/resources hold plain entity ids that
// are only meaningful against a ruleset branch. The editor soft-warns on ids
// missing from the current branch (placeholder rendering); the client
// hard-fails at game start.

export const MAP_SCHEMA_VERSION = 1;

// UI affordance only — the backend accepts any size in [MIN, MAX].
export const MAP_SIZE_PRESETS = [10, 16, 24, 32, 40];
export const MAP_SIZE_MIN = 8;
export const MAP_SIZE_MAX = 64;
export const MAP_SPAWNS_MIN = 2;
export const MAP_SPAWNS_MAX = 16;

export interface MapDocument {
    schemaVersion: number;
    // The storage id games pin. Never authored: '' until the first publish, when
    // the backend mints it from the English display name, and fixed after.
    name: string;
    versionNumber: number;                        // stamped by the backend; 0 = draft
    displayNames: { [lang: string]: string };     // "en" required to publish
    size: number;
    authoredAgainst?: { branch: string; versionNumber: number };
    // A palette ENTRY is the combo (tilePalette[i], tilePaletteImages[i]) —
    // ids may repeat with different pinned images.
    tilePalette: string[];
    // Optional, parallel to tilePalette: pinned asset-pack image key per
    // entry, null = client default (random pack variation). Visual only.
    tilePaletteImages?: (string | null)[];
    tileGrid: number[];                           // size*size palette indices, row-major y*size+x
    resources: { x: number; y: number; id: string; image?: string }[];
    spawns: { x: number; y: number }[];
    // Shuffle the spawn list before taking the first N, rather than using the
    // authored order. Optional: absent on maps authored before it existed, which the
    // client reads as false (spawn in order). Publishing a map with this set makes
    // the backend stamp schemaVersion 2, which older clients refuse rather than
    // playing in order and desyncing against a client that shuffled.
    spawnRandom?: boolean;
    metadata: MapMetadata;
    updatedAtUtc?: string;                        // stamped by the backend
}

export interface MapMetadata {
    spawnCount: number;
    waterFraction: number;        // fraction of tiles resolving to canSail
    impassableFraction: number;   // fraction of tiles resolving to !canMove
}

// One row of maps.json — the document minus its grids.
export interface MapManifestEntry {
    name: string;
    versionNumber: number;
    size: number;
    displayNames: { [lang: string]: string };
    metadata: MapMetadata;
    updatedAtUtc: string;
}

export function getMapDisplayName(entry: {name: string; displayNames?: {[lang: string]: string}}, lang = 'en'): string {
    return entry.displayNames?.[lang] || entry.displayNames?.['en'] || entry.name;
}

// The map's name as authored: the English display name, trimmed.
export function englishMapName(entry: {displayNames?: {[lang: string]: string}}): string {
    return (entry.displayNames?.['en'] ?? '').trim();
}

// The listed map, other than the one with id ownId, already called this. Players
// pick maps by name alone, so a clash blocks publishing (Maps.cs refuses it too).
export function findMapNameClash(name: string, entries: MapManifestEntry[], ownId: string): MapManifestEntry | undefined {
    const wanted = name.trim().toLowerCase();
    if (wanted === '') return undefined;
    return entries.find(entry => entry.name !== ownId && englishMapName(entry).toLowerCase() === wanted);
}

// A name for a duplicate of the map called name that no listed map has yet.
export function mapCopyName(name: string, entries: MapManifestEntry[]): string {
    let candidate = `${name} copy`;
    for (let n = 2; findMapNameClash(candidate, entries, '') !== undefined; n++) candidate = `${name} copy ${n}`;
    return candidate;
}

export function emptyMetadata(): MapMetadata {
    return {spawnCount: 0, waterFraction: 0, impassableFraction: 0};
}

// Create a fresh draft: a size×size grid filled with palette entry 0.
export function createMapDraft(size: number, firstTileId: string): MapDocument {
    return {
        schemaVersion: MAP_SCHEMA_VERSION,
        name: '',
        versionNumber: 0,
        displayNames: {},
        size,
        tilePalette: [firstTileId],
        tilePaletteImages: [null],
        tileGrid: new Array(size * size).fill(0),
        resources: [],
        spawns: [],
        metadata: emptyMetadata(),
    };
}

// The pinned image of a palette entry, or undefined for default.
export function paletteImageAt(doc: MapDocument, index: number): string | undefined {
    return doc.tilePaletteImages?.[index] ?? undefined;
}

// Does the tile at (x, y) allow this resource in the given branch?
//
// The rule the client enforces at generation time and the editor warns about, in one
// place: a tile lists the resource ids it permits. An unresolvable tile id is treated
// as permitting nothing, since nothing can be said about it.
export function resourceAllowedAt(
    doc: MapDocument,
    tiles: TileData[],
    x: number,
    y: number,
    resourceId: string,
): boolean {
    const tileId = doc.tilePalette[doc.tileGrid[y * doc.size + x]];
    const tile = tiles.find(t => t.id === tileId);
    return tile !== undefined && (tile.resources ?? []).includes(resourceId);
}

// Index of the (tileId, image) combo in the palette, or -1.
export function paletteIndexOf(doc: MapDocument, tileId: string, image: string | undefined): number {
    for (let i = 0; i < doc.tilePalette.length; i++) {
        if (doc.tilePalette[i] === tileId && paletteImageAt(doc, i) === image) return i;
    }
    return -1;
}

// Shape-validate a document from any untrusted source (localStorage, blobs).
// Deliberately does NOT check entity ids against the branch — maps are
// branch-agnostic; unresolvable ids render as placeholders. Returns undefined
// when the shape is beyond repair (wrong grid length, bad indices, unknown
// schema); forgiving elsewhere (missing arrays become empty, coords clamped
// out by filtering).
export function sanitizeMapDocument(raw: any): MapDocument | undefined {
    if (typeof raw !== 'object' || raw === null) return undefined;
    if (typeof raw.schemaVersion !== 'number' || raw.schemaVersion > MAP_SCHEMA_VERSION) return undefined;
    const size = raw.size;
    if (!Number.isInteger(size) || size < MAP_SIZE_MIN || size > MAP_SIZE_MAX) return undefined;
    if (!Array.isArray(raw.tilePalette) || raw.tilePalette.length === 0
        || !raw.tilePalette.every((id: unknown) => typeof id === 'string' && id.length > 0)) return undefined;
    if (!Array.isArray(raw.tileGrid) || raw.tileGrid.length !== size * size
        || !raw.tileGrid.every((i: unknown) => Number.isInteger(i) && (i as number) >= 0 && (i as number) < raw.tilePalette.length)) return undefined;

    const inBounds = (p: any) => Number.isInteger(p?.x) && Number.isInteger(p?.y)
        && p.x >= 0 && p.x < size && p.y >= 0 && p.y < size;
    const resources = Array.isArray(raw.resources)
        ? raw.resources.filter((r: any) => inBounds(r) && typeof r.id === 'string' && r.id.length > 0)
            .map((r: any) => ({
                x: r.x, y: r.y, id: r.id,
                ...(typeof r.image === 'string' && r.image.length > 0 ? {image: r.image} : {}),
            }))
        : [];

    // Pinned-variant images: visual-only, so a malformed array degrades to
    // "no pins" rather than rejecting the document.
    const tilePaletteImages = Array.isArray(raw.tilePaletteImages)
        && raw.tilePaletteImages.length === raw.tilePalette.length
        && raw.tilePaletteImages.every((key: unknown) => key === null || (typeof key === 'string' && key.length > 0))
        ? [...raw.tilePaletteImages] as (string | null)[]
        : undefined;
    const spawns = Array.isArray(raw.spawns)
        ? raw.spawns.filter(inBounds).map((s: any) => ({x: s.x, y: s.y}))
        : [];

    const displayNames: { [lang: string]: string } = {};
    if (typeof raw.displayNames === 'object' && raw.displayNames !== null) {
        for (const [lang, value] of Object.entries(raw.displayNames)) {
            if (typeof value === 'string') displayNames[lang] = value;
        }
    }

    return {
        schemaVersion: raw.schemaVersion,
        name: typeof raw.name === 'string' ? raw.name : '',
        versionNumber: Number.isInteger(raw.versionNumber) ? raw.versionNumber : 0,
        displayNames,
        size,
        authoredAgainst: typeof raw.authoredAgainst?.branch === 'string'
            ? {branch: raw.authoredAgainst.branch, versionNumber: raw.authoredAgainst.versionNumber ?? 0}
            : undefined,
        tilePalette: [...raw.tilePalette],
        tilePaletteImages,
        tileGrid: [...raw.tileGrid],
        resources,
        spawns,
        metadata: {
            spawnCount: spawns.length,
            waterFraction: typeof raw.metadata?.waterFraction === 'number' ? raw.metadata.waterFraction : 0,
            impassableFraction: typeof raw.metadata?.impassableFraction === 'number' ? raw.metadata.impassableFraction : 0,
        },
        updatedAtUtc: typeof raw.updatedAtUtc === 'string' ? raw.updatedAtUtc : undefined,
    };
}

// Derived metadata, computed against the given branch tiles. Palette ids the
// branch doesn't know count as neither water nor impassable — they surface
// through the warnings panel instead.
export function computeMapMetadata(doc: MapDocument, tiles: TileData[]): MapMetadata {
    const byId = new Map(tiles.map(t => [t.id, t]));
    let water = 0;
    let impassable = 0;
    for (const index of doc.tileGrid) {
        const tile = byId.get(doc.tilePalette[index]);
        if (!tile) continue;
        if (tile.canSail) water++;
        if (!tile.canMove) impassable++;
    }
    const total = doc.tileGrid.length || 1;
    return {
        spawnCount: doc.spawns.length,
        waterFraction: water / total,
        impassableFraction: impassable / total,
    };
}

// Crop/pad to a new size, keeping the overlapping top-left region. New cells
// fill with palette entry 0; out-of-bounds resources/spawns are dropped.
// Returns a NEW document.
export function resizeMapDocument(doc: MapDocument, newSize: number): MapDocument {
    const grid = new Array(newSize * newSize).fill(0);
    const overlap = Math.min(doc.size, newSize);
    for (let y = 0; y < overlap; y++) {
        for (let x = 0; x < overlap; x++) {
            grid[y * newSize + x] = doc.tileGrid[y * doc.size + x];
        }
    }
    const inBounds = (p: {x: number; y: number}) => p.x < newSize && p.y < newSize;
    return {
        ...doc,
        size: newSize,
        tileGrid: grid,
        tilePaletteImages: doc.tilePaletteImages ? [...doc.tilePaletteImages] : undefined,
        resources: doc.resources.filter(inBounds).map(r => ({...r})),
        spawns: doc.spawns.filter(inBounds).map(s => ({...s})),
        displayNames: {...doc.displayNames},
        metadata: {...doc.metadata},
    };
}

// Drop unused palette entries, merge duplicate (id, image) combos, and remap
// grid indices. Returns a NEW document (never mutates the draft — the undo
// stack holds index-based snapshots). tilePaletteImages is omitted entirely
// when no entry pins an image (keeps pin-free documents at the plain v1 shape).
export function compactPalette(doc: MapDocument): MapDocument {
    const used = new Set(doc.tileGrid);
    const remap = new Map<number, number>();
    const comboIndex = new Map<string, number>();
    const palette: string[] = [];
    const images: (string | null)[] = [];
    doc.tilePalette.forEach((id, index) => {
        if (!used.has(index)) return;
        const image = paletteImageAt(doc, index) ?? null;
        const combo = `${id}\n${image ?? ''}`;
        const existing = comboIndex.get(combo);
        if (existing !== undefined) {
            remap.set(index, existing);
            return;
        }
        comboIndex.set(combo, palette.length);
        remap.set(index, palette.length);
        palette.push(id);
        images.push(image);
    });
    return {
        ...doc,
        tilePalette: palette,
        tilePaletteImages: images.some(image => image !== null) ? images : undefined,
        tileGrid: doc.tileGrid.map(i => remap.get(i)!),
        resources: doc.resources.map(r => ({...r})),
        spawns: doc.spawns.map(s => ({...s})),
        displayNames: {...doc.displayNames},
        metadata: {...doc.metadata},
    };
}
